import { animate, type AnimationPlaybackControlsWithThen } from 'motion-v';
import { onScopeDispose, ref, watch } from 'vue';

export interface TypewriterTimings {
  /** Per letter, while typing */
  typeMs: number;
  /** Per letter, while erasing */
  eraseMs: number;
  /** The whole word stays on screen */
  holdMs: number;
  /** Empty field before the next word starts */
  pauseMs: number;
}

const TYPEWRITER_TIMINGS: TypewriterTimings = {
  typeMs: 100,
  eraseMs: 35,
  holdMs: 4000,
  pauseMs: 800,
};

/**
 * Types each word letter by letter, holds it, erases it and moves on to the
 * next one, forever. The letter count is animated by motion's `animate`, so
 * the pace is frame-driven and stops with the frameloop when the tab is hidden.
 *
 * Restarts from the first word whenever `words` change (a language switch),
 * and stays empty while `enabled` is false.
 */
export function useTypewriter(
  words: () => string[],
  enabled: () => boolean = () => true,
  timings: TypewriterTimings = TYPEWRITER_TIMINGS,
) {
  const text = ref('');
  let controls: AnimationPlaybackControlsWithThen | undefined;
  // Bumped on every restart: a loop awaiting a stopped animation must not go on.
  let generation = 0;

  function reveal(word: string, from: number, to: number, delayMs: number): Promise<void> {
    const perLetterMs = to > from ? timings.typeMs : timings.eraseMs;
    controls = animate(from, to, {
      duration: (Math.abs(to - from) * perLetterMs) / 1000,
      delay: delayMs / 1000,
      ease: 'linear',
      onUpdate: count => {
        text.value = word.slice(0, Math.round(count));
      },
    });
    return controls.then(() => undefined);
  }

  async function loop(current: number) {
    const cycle = words().filter(word => word.length > 0);
    if (!cycle.length) return;

    for (;;) {
      for (const word of cycle) {
        await reveal(word, 0, word.length, timings.pauseMs);
        if (current !== generation) return;
        await reveal(word, word.length, 0, timings.holdMs);
        if (current !== generation) return;
      }
    }
  }

  function stop() {
    generation++;
    controls?.stop();
    controls = undefined;
    text.value = '';
  }

  function restart() {
    stop();
    if (enabled()) loop(generation);
  }

  watch([words, enabled], restart, { immediate: true });
  onScopeDispose(stop);

  return { text };
}
